/**
 * The one genuinely-live piece of this app: real, unauthenticated `fetch` calls against
 * the real backend's `/healthz` and `/readyz` routes (`backend/app/main.py`, confirmed by
 * reading the source directly — both mounted at the app root, NOT under `/api/v1`, and
 * neither requires a bearer token). Deliberately kept out of `PlatformApiClient` — see
 * that interface's doc comment for why folding a real fetch into a "mock client" would be
 * misleading.
 *
 * `/healthz`: process liveness only, `{ status: "ok" }`, never touches the DB.
 * `/readyz`: independently verifies DB connectivity, `{ status: "ready" }` on success or a
 * 503 with `{ detail: "Database unavailable: ..." }` on failure — both response shapes
 * read directly from `backend/app/main.py`, not guessed.
 */
import type { HealthNode, HealthStatus } from "./types";
import { BACKEND_ROOT_URL } from "../env";

export interface LiveHealthResult {
  status: HealthStatus;
  detail: string;
  checkedAt: string;
}

async function checkEndpoint(path: "/healthz" | "/readyz"): Promise<LiveHealthResult> {
  const checkedAt = new Date().toISOString();
  try {
    const response = await fetch(`${BACKEND_ROOT_URL}${path}`, { method: "GET" });
    const body = (await response.json().catch(() => ({}))) as { status?: string; detail?: string };
    if (response.ok) {
      return { status: "healthy", detail: body.status ? `status: ${body.status}` : "OK", checkedAt };
    }
    return { status: "down", detail: body.detail ?? `HTTP ${response.status}`, checkedAt };
  } catch (error) {
    // Network failure (backend not running, CORS misconfiguration, offline) — surface as
    // "unknown" rather than "down": we genuinely don't know the backend's state, only
    // that this browser couldn't reach it, which is a meaningfully different signal for
    // an operator triaging an incident.
    const message = error instanceof Error ? error.message : "Request failed";
    return { status: "unknown", detail: `Could not reach backend: ${message}`, checkedAt };
  }
}

export function checkHealthz(): Promise<LiveHealthResult> {
  return checkEndpoint("/healthz");
}

export function checkReadyz(): Promise<LiveHealthResult> {
  return checkEndpoint("/readyz");
}

/** Wraps both live checks as `HealthNode`s (`mocked: false`) ready to merge into the same
 * grid `listHealthNodes()`'s mocked nodes render into — see `SystemHealthPage.tsx`. */
export async function fetchLiveHealthNodes(): Promise<HealthNode[]> {
  const [healthz, readyz] = await Promise.all([checkHealthz(), checkReadyz()]);
  return [
    {
      id: "backend-process",
      label: "FastAPI backend process (/healthz)",
      status: healthz.status,
      detail: healthz.detail,
      mocked: false,
      lastChecked: healthz.checkedAt,
    },
    {
      id: "postgres-primary",
      label: "Postgres primary connectivity (/readyz)",
      status: readyz.status,
      detail: readyz.detail,
      mocked: false,
      lastChecked: readyz.checkedAt,
    },
  ];
}
